import { Button } from '@/components/ui/Button';
import { Loading } from '@/components/ui/Loading';
import { useAuthStore } from '@/store/auth.store';
import { cn } from '@/utils/cn';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  FileText,
  Inbox,
  Mail,
  MessageCircle,
  Paperclip,
  Plus,
  RefreshCw,
  Send,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useSupportRequest } from '../hooks/useSupportRequest';
import {
  useSupportReply,
  useResolveSupportTicket,
  useSupportTicket,
  useSupportTickets,
} from '../hooks/useSupportTickets';
import { AdminSupportPage } from './AdminSupportPage';
import type {
  SupportTicket,
  SupportTicketStatus,
} from '../types/support.types';

const supportSchema = z.object({
  subject: z.string().trim().min(1, 'Le sujet est requis'),
  content: z
    .string()
    .trim()
    .min(10, 'Ajoutez quelques précisions (minimum 10 caractères)'),
});

type SupportFormData = z.infer<typeof supportSchema>;
type View = 'tickets' | 'new';

const STATUS_LABELS: Record<SupportTicketStatus, string> = {
  open: 'Envoyée',
  in_progress: 'En cours',
  waiting_for_user: 'Réponse reçue',
  resolved: 'Résolue',
  closed: 'Clôturée',
};

const STATUS_STYLES: Record<SupportTicketStatus, string> = {
  open: 'bg-warning-50 text-warning-800 ring-warning-200',
  in_progress: 'bg-primary-50 text-primary-800 ring-primary-200',
  waiting_for_user: 'bg-primary-50 text-primary-800 ring-primary-200',
  resolved: 'bg-success-50 text-success-800 ring-success-200',
  closed: 'bg-neutral-100 text-neutral-700 ring-neutral-200',
};

const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : dateFormatter.format(date);
}

function shortReference(uuid: string) {
  return uuid.slice(0, 8).toUpperCase();
}

function StatusBadge({ status }: { status: SupportTicketStatus }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset',
        STATUS_STYLES[status]
      )}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function EmailSourceBadge() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
      <Mail className="h-3 w-3" aria-hidden="true" />
      E-mail
    </span>
  );
}

function TicketRow({
  ticket,
  onSelect,
}: {
  ticket: SupportTicket;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="group focus-visible:ring-primary-500 flex w-full items-start justify-between gap-4 border-b border-neutral-100 px-5 py-4 text-left transition-colors last:border-b-0 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset sm:items-center"
      aria-label={`Ouvrir la demande ${ticket.subject}`}
    >
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate font-medium text-neutral-900">
            {ticket.subject}
          </p>
          {ticket.firstResponseAt && (
            <span className="text-primary-700 hidden shrink-0 text-xs font-medium sm:inline">
              Réponse reçue
            </span>
          )}
          {ticket.origin === 'email' && <EmailSourceBadge />}
        </div>
        <p className="mt-1 text-sm text-neutral-500">
          #{shortReference(ticket.uuid)} · {formatDate(ticket.createdAt)}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <StatusBadge status={ticket.status} />
        <ChevronRight className="h-4 w-4 text-neutral-400 transition-transform group-hover:translate-x-0.5" />
      </div>
    </button>
  );
}

function NewRequestForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SupportFormData>({
    resolver: zodResolver(supportSchema),
  });

  const { sendSupportRequest, isLoading } = useSupportRequest({
    onSuccess: () => {
      reset();
      setFiles([]);
      onClose();
    },
  });

  const onSubmit = (data: SupportFormData) => {
    sendSupportRequest({
      subject: data.subject,
      content: data.content,
      documents: files,
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (files.length + selectedFiles.length > 5) {
      setFileError('Vous pouvez joindre jusqu’à 5 fichiers.');
      event.currentTarget.value = '';
      return;
    }

    setFileError(null);
    setFiles(currentFiles => [...currentFiles, ...selectedFiles]);
    event.currentTarget.value = '';
  };

  const removeFile = (index: number) => {
    setFiles(currentFiles => currentFiles.filter((_, i) => i !== index));
    setFileError(null);
  };

  return (
    <section aria-labelledby="new-request-title" className="space-y-5">
      <div className="flex items-start gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="mt-0.5 -ml-2 shrink-0"
          aria-label="Revenir à mes demandes"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2
            id="new-request-title"
            className="text-xl font-semibold text-neutral-900"
          >
            Nouvelle demande
          </h2>
          <p className="mt-1 text-sm text-neutral-600">
            Décrivez ce qui bloque. Vous pourrez suivre la réponse ici.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-5 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6"
      >
        <div>
          <label
            htmlFor="subject"
            className="block text-sm font-medium text-neutral-700"
          >
            {t('support.form.subject', 'Sujet')}
          </label>
          <input
            {...register('subject')}
            id="subject"
            type="text"
            aria-invalid={Boolean(errors.subject)}
            className="focus:border-primary-500 focus:ring-primary-100 mt-2 block w-full rounded-xl border border-neutral-300 px-4 py-3 placeholder:text-neutral-400/50 focus:ring-2"
            placeholder={t(
              'support.form.subjectPlaceholder',
              'Résumez le problème en une phrase'
            )}
          />
          {errors.subject && (
            <p role="alert" className="text-error-600 mt-1.5 text-sm">
              {errors.subject.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="content"
            className="block text-sm font-medium text-neutral-700"
          >
            {t('support.form.content', 'Description')}
          </label>
          <textarea
            {...register('content')}
            id="content"
            rows={6}
            aria-invalid={Boolean(errors.content)}
            className="focus:border-primary-500 focus:ring-primary-100 mt-2 block w-full resize-y rounded-xl border border-neutral-300 px-4 py-3 placeholder:text-neutral-400/50 focus:ring-2"
            placeholder={t(
              'support.form.contentPlaceholder',
              'Que faisiez-vous ? Que s’est-il passé ? Quel résultat attendiez-vous ?'
            )}
          />
          {errors.content && (
            <p role="alert" className="text-error-600 mt-1.5 text-sm">
              {errors.content.message}
            </p>
          )}
        </div>

        <div>
          <div className="flex items-baseline justify-between gap-3">
            <label className="text-sm font-medium text-neutral-700">
              {t('support.form.attachments', 'Pièces jointes')}
            </label>
            <span className="text-xs text-neutral-500">
              Facultatif · 5 max.
            </span>
          </div>
          <label className="hover:border-primary-400 hover:bg-primary-50 mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-neutral-300 px-4 py-3 text-sm text-neutral-600 transition-colors">
            <Paperclip className="h-4 w-4 text-neutral-400" />
            Ajouter une capture ou un document
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt,.log"
              onChange={handleFileChange}
              className="sr-only"
              disabled={files.length >= 5}
            />
          </label>
          {fileError && (
            <p role="alert" className="text-error-600 mt-1.5 text-sm">
              {fileError}
            </p>
          )}

          {files.length > 0 && (
            <ul className="mt-3 space-y-2" aria-label="Fichiers sélectionnés">
              {files.map((file, index) => (
                <li
                  key={`${file.name}-${file.lastModified}-${index}`}
                  className="flex items-center justify-between gap-3 rounded-lg bg-neutral-50 px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-neutral-500" />
                    <span className="truncate text-sm text-neutral-700">
                      {file.name}
                    </span>
                    <span className="shrink-0 text-xs text-neutral-500">
                      {Math.max(1, Math.round(file.size / 1024))} Ko
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="focus-visible:ring-primary-500 rounded-lg p-1.5 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-900 focus-visible:ring-2 focus-visible:outline-none"
                    aria-label={`Retirer ${file.name}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <details className="group rounded-xl bg-neutral-50 px-4 py-3">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-neutral-700">
            Conseils pour obtenir une réponse utile
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" />
          </summary>
          <ul className="mt-3 space-y-1.5 text-sm text-neutral-600">
            <li>Indiquez le dossier ou l’automatisation concernée.</li>
            <li>Décrivez les étapes qui ont mené au problème.</li>
            <li>Joignez une capture si elle aide à comprendre.</li>
          </ul>
        </details>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            className="inline-flex items-center gap-2"
          >
            <Send className="h-4 w-4" />
            {isLoading ? 'Envoi en cours…' : 'Envoyer la demande'}
          </Button>
        </div>
      </form>
    </section>
  );
}

function TicketDetail({
  ticketUuid,
  onBack,
}: {
  ticketUuid: string;
  onBack: () => void;
}) {
  const {
    data: ticket,
    isLoading,
    isError,
    refetch,
  } = useSupportTicket(ticketUuid);
  const [reply, setReply] = useState('');
  const replyMutation = useSupportReply(ticketUuid);
  const resolveMutation = useResolveSupportTicket(ticketUuid);

  if (isLoading) {
    return <Loading className="py-16" />;
  }

  if (isError || !ticket) {
    return (
      <div className="border-error-200 bg-error-50 rounded-2xl border p-6 text-center">
        <AlertCircle className="text-error-600 mx-auto h-6 w-6" />
        <p className="text-error-900 mt-2 font-medium">
          Cette demande n’a pas pu être chargée.
        </p>
        <div className="mt-4 flex justify-center gap-2">
          <Button type="button" variant="ghost" onClick={onBack}>
            Retour
          </Button>
          <Button type="button" variant="outline" onClick={() => refetch()}>
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  const submitReply = (event: React.FormEvent) => {
    event.preventDefault();
    const content = reply.trim();
    if (!content) return;
    replyMutation.mutate(content, { onSuccess: () => setReply('') });
  };

  const isDone = ticket.status === 'resolved' || ticket.status === 'closed';

  return (
    <section aria-labelledby="ticket-title" className="space-y-5">
      <div className="flex items-start gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="mt-0.5 -ml-2 shrink-0"
          aria-label="Revenir à mes demandes"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2
              id="ticket-title"
              className="text-xl font-semibold text-neutral-900"
            >
              {ticket.subject}
            </h2>
            <StatusBadge status={ticket.status} />
            {ticket.origin === 'email' && <EmailSourceBadge />}
          </div>
          <p className="mt-1 text-sm text-neutral-500">
            Demande #{shortReference(ticket.uuid)} · envoyée le{' '}
            {formatDate(ticket.createdAt)}
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex items-center gap-2 text-sm font-medium text-neutral-700">
          <MessageCircle className="h-4 w-4" />
          Votre demande
        </div>
        <p className="mt-3 text-sm leading-6 whitespace-pre-wrap text-neutral-800">
          {ticket.content}
        </p>
        {ticket.attachments && ticket.attachments.length > 0 && (
          <div className="mt-4 border-t border-neutral-100 pt-3">
            <p className="text-xs font-medium text-neutral-500">
              {ticket.attachments.length} pièce
              {ticket.attachments.length > 1 ? 's' : ''} jointe
              {ticket.attachments.length > 1 ? 's' : ''}
            </p>
            <ul className="mt-2 space-y-1 text-sm text-neutral-700">
              {ticket.attachments.map((attachment, index) => (
                <li
                  key={`${attachment.filename}-${attachment.size}-${index}`}
                  className="flex items-center gap-2"
                >
                  <FileText className="h-4 w-4 text-neutral-400" />
                  {attachment.filename}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {ticket.replies.length > 0 && (
        <div className="space-y-3" aria-label="Échanges avec le support">
          {ticket.replies.map(message => {
            const fromSupport = message.authorRole === 'admin';
            return (
              <article
                key={message.uuid}
                className={cn(
                  'rounded-2xl border p-5',
                  fromSupport
                    ? 'border-primary-200 bg-primary-50'
                    : 'border-neutral-200 bg-white'
                )}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-neutral-900">
                    {fromSupport ? 'Équipe support' : 'Vous'}
                  </p>
                  <time
                    dateTime={message.createdAt}
                    className="text-xs text-neutral-500"
                  >
                    {formatDate(message.createdAt)}
                  </time>
                </div>
                <p className="mt-2 text-sm leading-6 whitespace-pre-wrap text-neutral-800">
                  {message.content}
                </p>
              </article>
            );
          })}
        </div>
      )}

      {ticket.closureSource === 'external_email' && (
        <div className="flex items-start gap-2 rounded-xl bg-neutral-100 px-4 py-3 text-sm text-neutral-700">
          <CheckCircle2 className="text-success-600 mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Cette demande a été clôturée après une réponse apportée par e-mail.
          </p>
        </div>
      )}

      <form
        onSubmit={submitReply}
        className="rounded-2xl border border-neutral-200 bg-white p-5"
      >
        <label
          htmlFor="support-reply"
          className="block text-sm font-medium text-neutral-700"
        >
          {isDone ? 'Le problème revient ?' : 'Ajouter un message'}
        </label>
        <textarea
          id="support-reply"
          value={reply}
          onChange={event => setReply(event.target.value)}
          rows={3}
          className="focus:border-primary-500 focus:ring-primary-100 mt-2 block w-full resize-y rounded-xl border border-neutral-300 px-4 py-3 placeholder:text-neutral-400/50 focus:ring-2"
          placeholder={
            isDone
              ? 'Décrivez ce qui reste à résoudre : la demande sera rouverte'
              : 'Ajoutez une précision ou répondez au support'
          }
        />
        <div className="mt-3 flex justify-end">
          <Button
            type="submit"
            disabled={!reply.trim() || replyMutation.isPending}
            className="gap-2"
          >
            <Send className="h-4 w-4" />
            {replyMutation.isPending
              ? 'Envoi…'
              : isDone
                ? 'Rouvrir et envoyer'
                : 'Envoyer'}
          </Button>
        </div>
      </form>

      {!isDone && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={() => resolveMutation.mutate()}
            disabled={resolveMutation.isPending}
            className="gap-2"
          >
            <CheckCircle2 className="h-4 w-4" />
            {resolveMutation.isPending
              ? 'Validation…'
              : 'Mon problème est résolu'}
          </Button>
        </div>
      )}
    </section>
  );
}

function TicketsList({ onSelect }: { onSelect: (uuid: string) => void }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, isError, refetch } =
    useSupportTickets(page);

  if (isLoading) {
    return <Loading className="py-16" />;
  }

  if (isError) {
    return (
      <div className="border-error-200 bg-error-50 rounded-2xl border p-6 text-center">
        <AlertCircle className="text-error-600 mx-auto h-6 w-6" />
        <p className="text-error-900 mt-2 font-medium">
          Vos demandes n’ont pas pu être chargées.
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => refetch()}
          className="mt-4 gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Réessayer
        </Button>
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-neutral-300 bg-white px-6 py-12 text-center">
        <Inbox className="mx-auto h-8 w-8 text-neutral-400" />
        <p className="mt-3 font-medium text-neutral-900">
          Aucune demande pour le moment
        </p>
        <p className="mt-1 text-sm text-neutral-500">
          Vos prochaines demandes et leurs réponses apparaîtront ici.
        </p>
      </div>
    );
  }

  return (
    <div className={cn(isFetching && 'opacity-70')} aria-live="polite">
      <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
        {data.data.map(ticket => (
          <TicketRow
            key={ticket.uuid}
            ticket={ticket}
            onSelect={() => onSelect(ticket.uuid)}
          />
        ))}
      </div>

      {data.pagination.totalPages > 1 && (
        <nav
          aria-label="Pagination des demandes"
          className="mt-4 flex items-center justify-between"
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPage(currentPage => currentPage - 1)}
            disabled={page <= 1 || isFetching}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Précédent
          </Button>
          <span className="text-sm text-neutral-500">
            Page {data.pagination.currentPage} sur {data.pagination.totalPages}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPage(currentPage => currentPage + 1)}
            disabled={page >= data.pagination.totalPages || isFetching}
            className="gap-1"
          >
            Suivant
            <ChevronRight className="h-4 w-4" />
          </Button>
        </nav>
      )}
    </div>
  );
}

function UserSupportPage() {
  const [view, setView] = useState<View>('tickets');
  const [selectedTicketUuid, setSelectedTicketUuid] = useState<string | null>(
    null
  );

  if (view === 'new') {
    return (
      <div className="mx-auto max-w-3xl">
        <NewRequestForm onClose={() => setView('tickets')} />
      </div>
    );
  }

  if (selectedTicketUuid) {
    return (
      <div className="mx-auto max-w-3xl">
        <TicketDetail
          ticketUuid={selectedTicketUuid}
          onBack={() => setSelectedTicketUuid(null)}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <header className="flex flex-col gap-4 border-b border-neutral-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Support</h1>
          <p className="mt-1 text-sm text-neutral-600">
            Retrouvez vos demandes et les réponses de l’équipe.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setView('new')}
          className="w-full gap-2 sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          Nouvelle demande
        </Button>
      </header>

      <TicketsList onSelect={setSelectedTicketUuid} />
    </div>
  );
}

export function SupportPage() {
  const role = useAuthStore(state => state.user?.role);
  return role === 'admin' ? <AdminSupportPage /> : <UserSupportPage />;
}
